/**
 * 🏢 COMPANY CONTEXT UTILITY
 * 
 * Provides company context management for multi-Company frontend
 * Extracts company from subdomain and provides utilities for API calls
 * 
 * SCHEMA STANDARD (Dec 2025):
 * - companyId is the ONLY company identifier (slug format like "ac-oil-gas")
 * - getCompanyId() is the primary function
 * - getCompanyId() kept as deprecated alias for backward compatibility
 */

/**
 * Get current company ID - prioritizes user's actual companyId from session
 * 
 * CRITICAL: This returns the companyId which is used for Firestore paths
 * like companies/{companyId}/wells/{wellId}
 * 
 * Priority:
 * 1. User's companyId from session (for actual company writes)
 * 2. SuperAdmin check (returns 'superadmin')
 * 3. Subdomain extraction (production)
 * 4. 'default' fallback
 * 
 * Examples:
 * - User with companyId "acme-oil" → "acme-oil"
 * - SuperAdmin user → "superadmin"
 * - acme-oil.oilwellx.com → "acme-oil"
 * - localhost:5173 with no session → "default"
 * 
 * @deprecated Use getCompanyId() for new code
 */


/**
 * Get current company ID (preferred function name)
 * Same as getCompanyId() but with clearer naming
 */
export function getCompanyId(): string {
  if (typeof window === 'undefined') return 'default';

  const hostname = window.location.hostname;

  // Check user session first - this is the ACTUAL company ID for writes
  try {
    // Check all possible storage keys
    const currentUser = localStorage.getItem('oilwellx_auth_session') ||
      localStorage.getItem('oilwellx_auth_user') ||
      sessionStorage.getItem('oilwellx_auth_session');

    if (currentUser) {
      const user = JSON.parse(currentUser);

      // SuperAdmin check - SECOND PRIORITY (they can operate across Companys)
      if (user.role === 'superadmin' || user.isSuperAdmin === true) {
        return 'superadmin';
      }

      // User's actual companyId - FIRST PRIORITY for regular users
      // This is the Firestore document ID under companies/{companyId}
      if (user.companyId && user.companyId !== 'default') {
        return user.companyId;
      }
      
      // Legacy fallback: check companyId (will be removed eventually)
      if (user.companyId && user.companyId !== 'default') {
        return user.companyId;
      }

      // Legacy fallback: check companyId (will be removed eventually)
      if (user.companyId && user.companyId !== 'default') {
        return user.companyId;
      }
    }
  } catch (e) {
  }

  // 🎨 Figma preview environment → default Company
  if (hostname.includes('figma')) {
    return 'default';
  }

  // Extract subdomain from companyname.oilwellx.com
  const parts = hostname.split('.');

  // If localhost or just oilwellx.com (no subdomain) → default
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1') || parts.length <= 2) {
    return 'default';
  }

  // Return subdomain (first part)
  return parts[0];
}

/**
 * Get the user's actual company ID from session
 * This is specifically for write operations like creating wells
 * Returns the companyId which is the Firestore document ID
 * Returns null if user doesn't have a valid company assigned
 */
export function getUserCompanyId(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const currentUser = localStorage.getItem('oilwellx_auth_session') ||
      localStorage.getItem('oilwellx_auth_user') ||
      sessionStorage.getItem('oilwellx_auth_session');

    if (currentUser) {
      const user = JSON.parse(currentUser);

      // SuperAdmin users don't have a single company
      if (user.role === 'superadmin' || user.isSuperAdmin === true) {
        return null;
      }

      // Return the user's actual companyId (Firestore path)
      if (user.companyId && user.companyId !== 'default') {
        return user.companyId;
      }
      
      // Legacy fallback: check companyId
      if (user.companyId && user.companyId !== 'default') {
        return user.companyId;
      }

      // Fallback to companyId if companyId not available
      if (user.companyId && user.companyId !== 'default') {
        return user.companyId;
      }
    }
  } catch (e) {
    console.error('❌ getUserCompanyId: Failed to parse user session:', e);
  }

  return null;
}

/**
 * Get Company display name (formatted for UI)
 */
export function getCompanyDisplayName(): string {
  const companyId = getCompanyId();

  if (companyId === 'default') {
    return 'OilWellX';
  }

  // Convert subdomain to display name
  // Example: "acme-oil" → "Acme Oil"
  return companyId
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get company-scoped localStorage key
 * 
 * This ensures localStorage data is isolated per company
 * Example: getCompanyStorageKey('wells') → 'Company:acme-oil:wells'
 */
export function getCompanyStorageKey(key: string): string {
  const companyId = getCompanyId();
  return `Company:${companyId}:${key}`;
}

/**
 * Get company headers for API requests
 * 
 * Always include these headers in API calls to ensure proper company routing
 */
export function getCompanyHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'X-Company-ID': getCompanyId(),
  };

  // Add user ID if logged in
  try {
    const currentUser = localStorage.getItem('oilwellx_auth_user') || sessionStorage.getItem('oilwellx_auth_session');
    if (currentUser) {
      const user = JSON.parse(currentUser);
      if (user.id || user.email) {
        headers['X-User-ID'] = user.id || user.email;
      }
    }
  } catch (e) {
    // Ignore parsing errors
  }

  return headers;
}

/**
 * Check if current user is SuperAdmin
 * 
 * SuperAdmin has special privileges across all Companys
 */
export function isSuperAdmin(user: any): boolean {
  return user?.role === 'superadmin' || user?.isSuperAdmin === true;
}

/**
 * Get Company subdomain for URLs
 * 
 * Useful for generating links to other Companys
 */
export function getCompanySubdomain(): string {
  const companyId = getCompanyId();
  return companyId === 'default' ? '' : companyId;
}

/**
 * Build Company-aware URL
 * 
 * Example: buildCompanyUrl('/dashboard') → 'https://acme-oil.oilwellx.com/dashboard'
 */
export function buildCompanyUrl(path: string, companyId?: string): string {
  const subdomain = companyId || getCompanySubdomain();
  const baseUrl = window.location.protocol + '//' + window.location.host;

  if (!subdomain || subdomain === 'default') {
    return baseUrl + path;
  }

  // Replace current subdomain with target subdomain
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  if (parts.length > 2) {
    parts[0] = subdomain;
    return window.location.protocol + '//' + parts.join('.') + path;
  }

  return baseUrl + path;
}

/**
 * company-scoped localStorage helpers
 */
export const companyStorage = {
  /**
   * Get item from company-scoped localStorage
   */
  getItem(key: string): string | null {
    return localStorage.getItem(getCompanyStorageKey(key));
  },

  /**
   * Set item in company-scoped localStorage
   */
  setItem(key: string, value: string): void {
    localStorage.setItem(getCompanyStorageKey(key), value);
  },

  /**
   * Remove item from company-scoped localStorage
   */
  removeItem(key: string): void {
    localStorage.removeItem(getCompanyStorageKey(key));
  },

  /**
   * Get JSON object from company-scoped localStorage
   */
  getJSON<T>(key: string, defaultValue?: T): T | null {
    const item = this.getItem(key);
    if (!item) return defaultValue || null;

    try {
      return JSON.parse(item) as T;
    } catch {
      return defaultValue || null;
    }
  },

  /**
   * Set JSON object in company-scoped localStorage
   */
  setJSON(key: string, value: any): void {
    this.setItem(key, JSON.stringify(value));
  },

  /**
   * Clear all company-scoped data
   */
  clear(): void {
    const companyId = getCompanyId();
    const prefix = `Company:${companyId}:`;

    // Remove all keys with Company prefix
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  },
};

/**
 * Log Company context for debugging
 */
export function logCompanyContext(): void {
  console.group('🏢 Company Context');
  console.groupEnd();
}

/**
 * Validate Company access (for admin/superadmin features)
 */
export function validateCompanyAccess(requiredRole: 'admin' | 'superadmin', currentUser: any): boolean {
  if (!currentUser) return false;

  if (requiredRole === 'superadmin') {
    return isSuperAdmin(currentUser);
  }

  if (requiredRole === 'admin') {
    return currentUser.role === 'admin' || isSuperAdmin(currentUser);
  }

  return false;
}

// Export as default for convenience
export default {
  getCompanyId,
  getCompanyDisplayName,
  getCompanyStorageKey,
  getCompanyHeaders,
  isSuperAdmin,
  getCompanySubdomain,
  buildCompanyUrl,
  companyStorage,
  logCompanyContext,
  validateCompanyAccess,
};

